import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { BasePageView } from "../../../common/components/BasePageView";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { DiceComboAction } from "../../../game/flow/impl/diceActions/DiceComboAction";
import { GiftDiceRollsAction } from "../../../game/flow/impl/diceActions/GiftDiceRollsAction";
import { GiftShieldsAction } from "../../../game/flow/impl/diceActions/GiftShieldsAction";
import { IGiftBoxPopupController } from "../controller/IGiftBoxPopupController";
import { GiftResourcePackage } from "../resources/GiftResourcePackage";
import { GiftBoxPage } from "./pages/GiftBoxPage";
import { GiftDiceRollsPage } from "./pages/summary/GiftDiceRollsPage";
import { GiftShieldsPage } from "./pages/summary/GiftShieldsPage";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { GiftPiggyWheelAction } from "../../../game/flow/impl/diceActions/GiftPiggyWheelAction";
import { HourlyBonusState } from "../../hourlyBonus/mainHourlyBonus/HourlyBonusState";

export class GiftBoxPopupView extends BaseScalableView<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly giftResources: GiftResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly playerModel: IPlayerModel;
    private readonly giftBoxPopupController: IGiftBoxPopupController;
    private readonly action: DiceComboAction;
    private readonly isFirstShow: boolean;
    private readonly topPanel: TopPanel;
    private readonly logger: ILogger;

    public constructor(
        commonResources: CommonResourcesPackage,
        giftResources: GiftResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        playerModel: IPlayerModel,
        giftBoxPopupController: IGiftBoxPopupController,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<void>,
        action: DiceComboAction,
        isFirstShow: boolean,
        topPanel: TopPanel,
        logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.giftResources = giftResources;
        this.soundResources = soundResources;
        this.playerModel = playerModel;
        this.giftBoxPopupController = giftBoxPopupController;
        this.action = action;
        this.isFirstShow = isFirstShow;
        this.topPanel = topPanel;
        this.logger = logger;
    }

    public async start(): Promise<void> {
        const giftBoxPageCS = new PromiseCompletionSource<void>();
        const giftBox = new GiftBoxPage(
            this.giftResources,
            this.commonResources,
            this.soundResources,
            this.canvasScaler,
            this.action,
            giftBoxPageCS
        );
        this.addChild(giftBox);
        await giftBoxPageCS.result;
        await this.showGiftSummaryPage(giftBox);
        this.operationControls.complete();
    }

    private async showGiftSummaryPage(firstPage: BasePageView): Promise<void> {
        switch (this.action.constructor) {
            case GiftDiceRollsAction:
                this.removeChild(firstPage);
                await this.showGiftDiceRollsPage(this.action as GiftDiceRollsAction);
                break;
            case GiftShieldsAction:
                await this.buildGiftShieldsPage(this.action as GiftShieldsAction);
                break;
            case GiftPiggyWheelAction:
                this.removeChild(firstPage);
                await this.buildGiftPiggyWheelPage();
                break;
            default:
                this.logger.warn(`Page not implemented : ${this.action.constructor.name}.`);
                this.operationControls.complete();
        }
    }

    private async showGiftDiceRollsPage(diceRollAction: GiftDiceRollsAction): Promise<void> {
        const giftDiceRollsPageCS = new PromiseCompletionSource<void>();
        const giftDiceRollsPage = new GiftDiceRollsPage(this.commonResources,
            this.giftResources,
            this.soundResources,
            this.canvasScaler,
            this.playerModel,
            this.giftBoxPopupController,
            diceRollAction,
            giftDiceRollsPageCS
        );
        this.addChild(giftDiceRollsPage);
        await giftDiceRollsPageCS.result;
    }

    private async buildGiftShieldsPage(shieldsAction: GiftShieldsAction): Promise<void> {
        const giftShieldsPageCS = new PromiseCompletionSource<void>();
        const giftShieldsPage = new GiftShieldsPage(this.commonResources,
            this.giftResources,
            this.soundResources,
            this.canvasScaler,
            this.giftBoxPopupController,
            shieldsAction,
            giftShieldsPageCS,
            this.isFirstShow,
            this.topPanel
        );
        this.addChild(giftShieldsPage);
        await giftShieldsPageCS.result;
    }

    private async buildGiftPiggyWheelPage(): Promise<void> {
        await this.giftBoxPopupController.showHourlyBonusPopup(HourlyBonusState.PIGGY);
    }
}
